import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Clock, BookOpen, TrendingUp } from 'lucide-react';

const MathErrorLog = () => {
  // Load logs from localStorage on initial load
  const [logs, setLogs] = useState(() => {
    const savedLogs = localStorage.getItem('mathErrorLogs');
    return savedLogs ? JSON.parse(savedLogs) : [];
  });

  const [newLog, setNewLog] = useState({
    topic: '',
    subtopic: '',
    question: '',
    error: '',
    correct_answer: '',
    your_answer: ''
  });

  const [filter, setFilter] = useState('ALL');

  // Save logs to localStorage whenever they change
  React.useEffect(() => {
    localStorage.setItem('mathErrorLogs', JSON.stringify(logs));
  }, [logs]);

  const addLog = () => {
    if (!newLog.topic || !newLog.question) return;

    const level = newLog.error ? 'ERROR' : 'SUCCESS';
    const severity = newLog.error ? 'HIGH' : 'NONE';
    
    const log = {
      timestamp: new Date().toISOString(),
      level,
      topic: newLog.topic,
      subtopic: newLog.subtopic || 'General',
      question: newLog.question,
      error: newLog.error || null,
      correct_answer: newLog.correct_answer,
      your_answer: newLog.your_answer,
      severity,
      retry_count: 0,
      mastered: level === 'SUCCESS'
    };

    setLogs([log, ...logs]);
    setNewLog({
      topic: '',
      subtopic: '',
      question: '',
      error: '',
      correct_answer: '',
      your_answer: ''
    });
  };

  const getLevelColor = (level) => {
    switch(level) {
      case 'ERROR': return 'text-red-600';
      case 'WARN': return 'text-yellow-600';
      case 'SUCCESS': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      HIGH: 'bg-red-100 text-red-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      LOW: 'bg-blue-100 text-blue-800',
      NONE: 'bg-green-100 text-green-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const filteredLogs = logs.filter(log => {
    if (filter === 'ALL') return true;
    return log.level === filter;
  });

  const stats = {
    total: logs.length,
    errors: logs.filter(l => l.level === 'ERROR').length,
    warnings: logs.filter(l => l.level === 'WARN').length,
    success: logs.filter(l => l.level === 'SUCCESS').length,
    mastered: logs.filter(l => l.mastered).length
  };

  const clearAllLogs = () => {
    if (window.confirm('Are you sure you want to delete all logs? This cannot be undone!')) {
      setLogs([]);
      localStorage.removeItem('mathErrorLogs');
    }
  };

  const exportLogs = () => {
    const dataStr = JSON.stringify(logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `math-error-logs-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <BookOpen className="text-blue-400" />
            Math Study Error Log System
          </h1>
          <p className="text-gray-400">Track your learning progress with cybersecurity-style logging</p>
        </div>

        {/* Stats Dashboard */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
            <div className="text-gray-400 text-sm">Total Logs</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg border border-red-900">
            <div className="text-red-400 text-sm">Errors</div>
            <div className="text-2xl font-bold text-red-500">{stats.errors}</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg border border-yellow-900">
            <div className="text-yellow-400 text-sm">Warnings</div>
            <div className="text-2xl font-bold text-yellow-500">{stats.warnings}</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg border border-green-900">
            <div className="text-green-400 text-sm">Success</div>
            <div className="text-2xl font-bold text-green-500">{stats.success}</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg border border-blue-900">
            <div className="text-blue-400 text-sm flex items-center gap-1">
              <TrendingUp size={14} />
              Mastered
            </div>
            <div className="text-2xl font-bold text-blue-500">{stats.mastered}</div>
          </div>
        </div>

        {/* Add New Log */}
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 mb-6">
          <h2 className="text-xl font-semibold mb-4">Add New Log Entry</h2>
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Topic (e.g., Functions, Calculus)"
              value={newLog.topic}
              onChange={(e) => setNewLog({...newLog, topic: e.target.value})}
              className="bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="Subtopic (e.g., Domain & Range)"
              value={newLog.subtopic}
              onChange={(e) => setNewLog({...newLog, subtopic: e.target.value})}
              className="bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="Question"
              value={newLog.question}
              onChange={(e) => setNewLog({...newLog, question: e.target.value})}
              className="bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm col-span-2"
            />
            <input
              type="text"
              placeholder="Your Answer"
              value={newLog.your_answer}
              onChange={(e) => setNewLog({...newLog, your_answer: e.target.value})}
              className="bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="Correct Answer"
              value={newLog.correct_answer}
              onChange={(e) => setNewLog({...newLog, correct_answer: e.target.value})}
              className="bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="Error/Mistake (leave empty if correct)"
              value={newLog.error}
              onChange={(e) => setNewLog({...newLog, error: e.target.value})}
              className="bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm col-span-2"
            />
          </div>
          <button
            onClick={addLog}
            className="mt-4 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-sm font-semibold"
          >
            Add Log Entry
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4 items-center">
          <div className="flex gap-2 flex-1">
            {['ALL', 'ERROR', 'WARN', 'SUCCESS'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded text-sm font-semibold ${
                  filter === f 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            onClick={exportLogs}
            className="px-4 py-2 rounded text-sm font-semibold bg-green-700 hover:bg-green-800 text-white"
            disabled={logs.length === 0}
          >
            Export Logs
          </button>
          <button
            onClick={clearAllLogs}
            className="px-4 py-2 rounded text-sm font-semibold bg-red-700 hover:bg-red-800 text-white"
            disabled={logs.length === 0}
          >
            Clear All
          </button>
        </div>

        {/* Log Entries */}
        <div className="space-y-2">
          {filteredLogs.map((log, idx) => (
            <div 
              key={idx}
              className="bg-gray-800 border border-gray-700 rounded-lg p-4 font-mono text-sm hover:border-gray-600 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 text-gray-500">
                  <Clock size={16} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-gray-500">{log.timestamp}</span>
                    <span className={`font-bold ${getLevelColor(log.level)}`}>
                      [{log.level}]
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getSeverityBadge(log.severity)}`}>
                      {log.severity}
                    </span>
                    {log.mastered && (
                      <span className="flex items-center gap-1 text-green-400 text-xs">
                        <CheckCircle size={14} />
                        MASTERED
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-1 text-gray-300">
                    <div>
                      <span className="text-blue-400">topic:</span> {log.topic} | 
                      <span className="text-blue-400"> subtopic:</span> {log.subtopic}
                    </div>
                    <div>
                      <span className="text-purple-400">question:</span> {log.question}
                    </div>
                    {log.error && (
                      <div className="text-red-400">
                        <AlertCircle size={14} className="inline mr-1" />
                        <span className="text-red-300">error:</span> {log.error}
                      </div>
                    )}
                    <div>
                      <span className="text-yellow-400">your_answer:</span> {log.your_answer}
                    </div>
                    <div>
                      <span className="text-green-400">correct_answer:</span> {log.correct_answer}
                    </div>
                    <div className="text-gray-500 text-xs">
                      retry_count: {log.retry_count} | mastered: {log.mastered.toString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredLogs.length === 0 && (
          <div className="text-center text-gray-500 py-12">
            No logs found for filter: {filter}
          </div>
        )}
      </div>
    </div>
  );
};

export default MathErrorLog;